import { getTranslations } from '@assets/i18n/config';
import { FinancialProductStrategy } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { AvailableSecurity } from '@ovComponents/3-pattern/securitiesReviewDialog/securitiesReviewDialog';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { calculateDurationSeconds } from '@utils/analytics/eventMetrics';
import { appendSecurityRows } from '@utils/appendSecurityRows';
import { useToast } from 'hephaestus/web';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import { normalizeCashSecurity } from '../utils/normalizeCashSecurity';
import {
  compactTaxRanksAfterRemoval,
  getEffectiveTaxRankAtIndex,
} from '../utils/useAssetClassGrouping';
import { useCsvSecuritiesImport } from '../utils/useCsvSecuritiesImport';
import { useModelPortfolioCash } from '../utils/useModelPortfolioCash';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';
import { AddModelPortfolioSecuritiesVisual } from './addModelPortfolioSecurities.visual';
import { withSecuritiesStepDefaults } from './securitiesStepOptions';

const TEMP_ID_PREFIX = 'temp-';

export const AddModelPortfolioSecurities = ({
  options: configuredOptions,
  onNext,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const options = useMemo(
    () => withSecuritiesStepDefaults(configuredOptions),
    [configuredOptions],
  );
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const { t } = useTranslation(['buildModels']);
  const { workflowData, setWorkflowData, updateWorkflowContext } =
    useContext(WorkflowContext);
  const [stepStartTime, setStepStartTime] = useState<Date | null>(null);
  // Every branch that reaches this step is meant to stage the model it edits
  // before it — the shelf flow creates one, the household branches load the
  // household's own — but an admin can order it ahead of the step that does.
  // An empty stand-in leaves the advisor an empty table to make sense of
  // rather than a step that throws on a model that is not there.
  const [modelPortfolioDetails, setModelPortfolioDetails] =
    useState<ModelPortfolioData>(
      workflowData.modelPortfolioDetails ?? ({} as ModelPortfolioData),
    );
  // Read once, and from a ref so that reporting to the context — which hands
  // back a new context, and a new model with it — cannot be mistaken for the
  // model having been staged after all.
  const arrivedWithAModel = useRef(!!workflowData.modelPortfolioDetails);
  const [securities, setSecurities] = useState<Security[]>(
    modelPortfolioDetails?.securities || [],
  );
  const { saveModelPortfolio, loading } = useSaveModelPortfolio();
  const [showImportPrompt, setShowImportPrompt] = useState(false);

  const updateSecuritiesState = useCallback((newSecurities: Security[]) => {
    setSecurities(newSecurities);
    setModelPortfolioDetails((prev) => ({
      ...prev,
      securities: newSecurities,
    }));
  }, []);

  const isAssetClassStrategy =
    modelPortfolioDetails.strategy === FinancialProductStrategy.AssetClass;

  const tableOptions = useMemo(() => {
    if (!isAssetClassStrategy) return options;

    const columns = options.securityAllocationChart?.columns?.[0];
    if (!columns) return options;

    return {
      ...options,
      securityAllocationChart: {
        ...options.securityAllocationChart,
        columns: [
          {
            ...columns,
            portfolioPercentage: {
              enabled: true,
              label:
                columns.portfolioPercentage?.label ??
                getTranslations({
                  fileName: 'modelPortfolios',
                  translationPath: [
                    'securityTableColumns',
                    'portfolioPercentage',
                  ],
                }),
            },
          },
        ],
      },
    };
  }, [options, isAssetClassStrategy]);

  const stableAssetClassBranches = useMemo(
    () => modelPortfolioDetails.assetClassBranches ?? [],
    [modelPortfolioDetails.assetClassBranches],
  );

  const { hasCashBranch, cashLoading, cashProduct, cashError } =
    useModelPortfolioCash(
      stableAssetClassBranches,
      modelPortfolioDetails.assetClassLevel,
    );

  useEffect(() => {
    if (cashError) {
      enqueueToast(t('cashFetchError'), { variant: 'error' });
    }
  }, [cashError, enqueueToast, t]);

  const onCsvImportComplete = useCallback(
    (imported: Security[]) => {
      updateSecuritiesState(
        imported.map((s) => normalizeCashSecurity(s, cashProduct)),
      );
    },
    [updateSecuritiesState, cashProduct],
  );

  const {
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    pendingImportData,
    handleImportFromCsv,
    handleReviewSubmit: handleReviewSubmitFromHook,
    handleReviewClose,
  } = useCsvSecuritiesImport({
    existingSecurities: securities,
    onImportComplete: onCsvImportComplete,
    isAssetClassStrategy,
  });

  useEffect(() => {
    setStepStartTime(new Date());
  }, []);

  // Reached through a ref rather than watched. The provider builds a new one of
  // these on every render and calling it renders the provider, so an effect
  // that lists it writes the whole workflow context, is handed a different
  // function, and writes it again — a round trip a second for as long as the
  // advisor is on the step, none of it saying anything new.
  const reportToWorkflow = useRef(setWorkflowData);
  reportToWorkflow.current = setWorkflowData;

  useEffect(() => {
    // Only ever a report on the model the step was handed. With none staged,
    // rows written here would sit under the key a model portfolio is kept
    // under, where the step after this one could take them for a model.
    if (!arrivedWithAModel.current) return;

    void reportToWorkflow.current((prevWorkflowData) => ({
      ...prevWorkflowData,
      modelPortfolioDetails: {
        ...modelPortfolioDetails,
        securities,
      },
    }));
  }, [securities, modelPortfolioDetails]);

  useEffect(() => {
    if (securities.length === 0) {
      setShowImportPrompt(true);
    }
  }, []);

  const handleAddSecurities = (products: FinancialProduct[]) => {
    updateSecuritiesState(appendSecurityRows(securities, products));
  };

  const handleAddCash = () => {
    if (cashLoading) return;
    if (securities.some((s) => s.financialProduct.isCash)) return;

    if (!cashProduct) {
      enqueueToast(t('buildModels:noCashAssetClassFound'), {
        variant: 'error',
      });
      return;
    }

    // cashProduct.primaryAssetClass (or secondary/tertiary based on assetClassLevel)
    // is sourced from the DB so the visual's groupedSecurities filter can correctly
    // match this security to its asset class branch
    const newCashSecurity: Security = {
      financialProduct: cashProduct,
      percentage: 0,
      isCashEquivalent: false,
      taxRank: undefined,
      id: `${TEMP_ID_PREFIX}cash-${cashProduct.id}`,
    };

    updateSecuritiesState([...securities, newCashSecurity]);
  };

  const handleUpdateSecurity = (index: number, security: Security) => {
    const updated = [...securities];
    const current = updated[index];
    let next = { ...security };

    if (
      current.financialProduct.id?.startsWith(TEMP_ID_PREFIX) &&
      !security.financialProduct.id
    ) {
      next.financialProduct.id = current.financialProduct.id;
      next.id = current.financialProduct.id;
    }

    next = normalizeCashSecurity(next, cashProduct);

    updated[index] = next;
    updateSecuritiesState(updated);
  };

  // Ref avoids adding `securities` to the effect deps below (stale-closure guard).
  const securitiesRef = useRef(securities);
  securitiesRef.current = securities;

  // Tracks the last cashProduct.id normalised so the effect is idempotent
  // across re-renders where cashProduct gets a new object reference.
  const normalizedForCashIdRef = useRef<string | undefined>(undefined);

  // Re-normalises cash securities already in state when cashProduct resolves
  // (handles the race where saved state loads before the query returns).
  useEffect(() => {
    if (!cashProduct) return;
    if (normalizedForCashIdRef.current === cashProduct.id) return;
    const hasCashSecurity = securitiesRef.current.some(
      (s) =>
        s.financialProduct.isCash || s.financialProduct.id === cashProduct.id,
    );
    if (!hasCashSecurity) return;
    normalizedForCashIdRef.current = cashProduct.id;
    updateSecuritiesState(
      securitiesRef.current.map((s) => normalizeCashSecurity(s, cashProduct)),
    );
  }, [cashProduct, updateSecuritiesState]);

  const isCashSecurity = useCallback(
    (s: Security) =>
      s.financialProduct.isCash ||
      (cashProduct != null && s.financialProduct.id === cashProduct.id),
    [cashProduct],
  );

  const handleRemoveSecurity = (index: number) => {
    const removedTaxRank = getEffectiveTaxRankAtIndex(
      securities,
      index,
      isCashSecurity,
    );
    const filtered = securities.filter((_, i) => i !== index);
    const compacted = compactTaxRanksAfterRemoval(
      filtered,
      isCashSecurity,
      removedTaxRank,
    ).map((s) => normalizeCashSecurity(s, cashProduct));
    updateSecuritiesState(compacted);
  };

  const handleUpdateTaxRank = (index: number, value: number) => {
    const updated = [...securities];
    updated[index] = { ...updated[index], taxRank: Number(value) };
    updateSecuritiesState(updated);
  };

  const handleUpdateTargetPercentage = (index: number, value: number) => {
    const updated = [...securities];
    updated[index] = { ...updated[index], percentage: value };
    updateSecuritiesState(updated);
  };

  const handleReorderSecurities = (reordered: Security[]) => {
    const cashSecurities = reordered.filter(isCashSecurity);
    const nonCashSecurities = reordered.filter((s) => !isCashSecurity(s));

    const updated = [...nonCashSecurities, ...cashSecurities].map((s, i) => {
      const normalized = normalizeCashSecurity(s, cashProduct);
      return {
        ...normalized,
        financialProduct: { ...normalized.financialProduct },
        id: normalized.id || normalized.financialProduct.id,
        // Honour an explicit taxRank already set by the caller (e.g. drag-and-drop
        // pre-assigns i+1; non-reordering callers preserve the original value).
        // Fall back to positional rank only when no taxRank is provided.
        taxRank: isCashSecurity(s) ? undefined : (s.taxRank ?? i + 1),
      };
    });

    updateSecuritiesState(updated);
  };

  const continueFunc = async () => {
    if (arrivedWithAModel.current) {
      await setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: {
          ...workflowData.modelPortfolioDetails,
          securities,
        },
      });
    }

    if (updateWorkflowContext) await updateWorkflowContext();

    const stepEndTime = new Date();
    const formDuration = calculateDurationSeconds(stepStartTime, {
      endTime: stepEndTime,
    });

    sendAnalytic(ovAnalyticsEvents.modelPortfolioWorkflowStep3Complete, {
      stepNumber: 3,
      stepName: 'ADD_MODEL_PORTFOLIO_SECURITIES',
      formDuration,
      timestamp: stepEndTime.toISOString(),
      workflowCompletionId: workflowCompletion?.id,
    });

    onNext();
  };

  const handleSaveForLater = async () => {
    const savedData = await saveModelPortfolio(modelPortfolioDetails);
    setModelPortfolioDetails({ ...savedData, originalValues: savedData });
  };

  const handleReviewSubmit = async (replacements: Record<string, string>) => {
    try {
      await handleReviewSubmitFromHook(replacements);
    } catch (error) {
      enqueueToast(error instanceof Error ? error.message : String(error), {
        variant: 'error',
      });
    }
  };

  return (
    <AddModelPortfolioSecuritiesVisual
      options={tableOptions}
      workflowCompletion={workflowCompletion}
      securities={securities}
      continueFunc={continueFunc}
      onAddSecurities={handleAddSecurities}
      onAddCash={
        isAssetClassStrategy && (hasCashBranch || cashLoading)
          ? handleAddCash
          : undefined
      }
      onUpdateSecurity={handleUpdateSecurity}
      onRemoveSecurity={handleRemoveSecurity}
      onUpdateTaxRank={handleUpdateTaxRank}
      onUpdateTargetPercentage={handleUpdateTargetPercentage}
      onSaveForLater={handleSaveForLater}
      onImportFromCsv={handleImportFromCsv}
      onReorderSecurities={handleReorderSecurities}
      modelPortfolioDetails={modelPortfolioDetails}
      loading={loading || cashLoading}
      importLoading={importLoading}
      showImportPrompt={showImportPrompt}
      onCloseImportPrompt={() => setShowImportPrompt(false)}
      showReviewDialog={showReviewDialog}
      unrecognizedSecurities={unrecognizedSecurities}
      availableSecurities={
        (pendingImportData?.availableSecurities ?? []) as AvailableSecurity[]
      }
      onReviewSubmit={handleReviewSubmit}
      onReviewClose={handleReviewClose}
    />
  );
};

export default AddModelPortfolioSecurities;
